import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { X, Save, Loader2 } from 'lucide-react';
import { RoomType } from '../../services/roomService';

interface RoomTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (roomData: Partial<RoomType>) => Promise<void>;
  roomType?: RoomType | null;
  hotelId: number;
}

const RoomTypeModal: React.FC<RoomTypeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  roomType,
  hotelId
}) => {
  const [formData, setFormData] = useState({
    hotel_id: hotelId,
    room_type_name: '',
    room_description: '',
    room_size_sqft: '',
    bed_type: 'King' as RoomType['bed_type'],
    number_of_beds: 1,
    max_adults: 2,
    max_children: 2,
    max_total_occupancy: 4,
    extra_bed_available: false,
    max_extra_beds: 0,
    extra_bed_charge: 0,
    smoking_preference: 'Non-Smoking' as RoomType['smoking_preference'],
    total_rooms: 1,
    rooms_allocated_to_trp: 1,
    status: 'active' as RoomType['status'],
    display_order: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (roomType) {
      setFormData({
        hotel_id: roomType.hotel_id,
        room_type_name: roomType.room_type_name,
        room_description: roomType.room_description || '',
        room_size_sqft: roomType.room_size_sqft?.toString() || '',
        bed_type: roomType.bed_type,
        number_of_beds: roomType.number_of_beds,
        max_adults: roomType.max_adults,
        max_children: roomType.max_children,
        max_total_occupancy: roomType.max_total_occupancy,
        extra_bed_available: roomType.extra_bed_available,
        max_extra_beds: roomType.max_extra_beds,
        extra_bed_charge: roomType.extra_bed_charge,
        smoking_preference: roomType.smoking_preference,
        total_rooms: roomType.total_rooms,
        rooms_allocated_to_trp: roomType.rooms_allocated_to_trp,
        status: roomType.status,
        display_order: roomType.display_order
      });
    } else {
      setFormData({
        hotel_id: hotelId,
        room_type_name: '',
        room_description: '',
        room_size_sqft: '',
        bed_type: 'King',
        number_of_beds: 1,
        max_adults: 2,
        max_children: 2,
        max_total_occupancy: 4,
        extra_bed_available: false,
        max_extra_beds: 0,
        extra_bed_charge: 0,
        smoking_preference: 'Non-Smoking',
        total_rooms: 1,
        rooms_allocated_to_trp: 1,
        status: 'active',
        display_order: 0
      });
    }
  }, [roomType, hotelId]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        room_size_sqft: formData.room_size_sqft ? parseInt(formData.room_size_sqft) : undefined,
        extra_bed_charge: parseFloat(formData.extra_bed_charge.toString())
      };

      await onSave(submitData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save room type');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="text-white">
            {roomType ? 'Edit Room Type' : 'Add New Room Type'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-md text-red-400">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="room_type_name" className="text-gray-300">Room Type Name *</Label>
                <Input
                  id="room_type_name"
                  value={formData.room_type_name}
                  onChange={(e) => handleInputChange('room_type_name', e.target.value)}
                  placeholder="e.g., Deluxe King Room"
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label htmlFor="bed_type" className="text-gray-300">Bed Type *</Label>
                <select
                  id="bed_type"
                  value={formData.bed_type}
                  onChange={(e) => handleInputChange('bed_type', e.target.value)}
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                  required
                >
                  <option value="King">King</option>
                  <option value="Queen">Queen</option>
                  <option value="Twin">Twin</option>
                  <option value="Double">Double</option>
                  <option value="Single">Single</option>
                  <option value="Sofa Bed">Sofa Bed</option>
                </select>
              </div>

              <div>
                <Label htmlFor="number_of_beds" className="text-gray-300">Number of Beds</Label>
                <Input
                  id="number_of_beds"
                  type="number"
                  min="1"
                  value={formData.number_of_beds}
                  onChange={(e) => handleInputChange('number_of_beds', parseInt(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label htmlFor="room_size_sqft" className="text-gray-300">Room Size (sq ft)</Label>
                <Input
                  id="room_size_sqft"
                  type="number"
                  min="0"
                  value={formData.room_size_sqft}
                  onChange={(e) => handleInputChange('room_size_sqft', e.target.value)}
                  placeholder="e.g., 350"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            {/* Occupancy */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="max_adults" className="text-gray-300">Max Adults *</Label>
                <Input
                  id="max_adults"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_adults}
                  onChange={(e) => handleInputChange('max_adults', parseInt(e.target.value))}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label htmlFor="max_children" className="text-gray-300">Max Children *</Label>
                <Input
                  id="max_children"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.max_children}
                  onChange={(e) => handleInputChange('max_children', parseInt(e.target.value))}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label htmlFor="max_total_occupancy" className="text-gray-300">Max Total Occupancy *</Label>
                <Input
                  id="max_total_occupancy"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_total_occupancy}
                  onChange={(e) => handleInputChange('max_total_occupancy', parseInt(e.target.value))}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            {/* Inventory */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_rooms" className="text-gray-300">Total Rooms *</Label>
                <Input
                  id="total_rooms"
                  type="number"
                  min="1"
                  value={formData.total_rooms}
                  onChange={(e) => handleInputChange('total_rooms', parseInt(e.target.value))}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label htmlFor="rooms_allocated_to_trp" className="text-gray-300">Rooms Allocated to TRP *</Label>
                <Input
                  id="rooms_allocated_to_trp"
                  type="number"
                  min="1"
                  value={formData.rooms_allocated_to_trp}
                  onChange={(e) => handleInputChange('rooms_allocated_to_trp', parseInt(e.target.value))}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            {/* Extra Bed Options */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="extra_bed_available"
                  checked={formData.extra_bed_available}
                  onChange={(e) => handleInputChange('extra_bed_available', e.target.checked)}
                  className="rounded border-gray-700 bg-gray-800"
                />
                <Label htmlFor="extra_bed_available" className="text-gray-300">Extra Bed Available</Label>
              </div>

              {formData.extra_bed_available && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="max_extra_beds" className="text-gray-300">Max Extra Beds</Label>
                    <Input
                      id="max_extra_beds"
                      type="number"
                      min="0"
                      value={formData.max_extra_beds}
                      onChange={(e) => handleInputChange('max_extra_beds', parseInt(e.target.value))}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="extra_bed_charge" className="text-gray-300">Extra Bed Charge (₹)</Label>
                    <Input
                      id="extra_bed_charge"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.extra_bed_charge}
                      onChange={(e) => handleInputChange('extra_bed_charge', parseFloat(e.target.value))}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preferences */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smoking_preference" className="text-gray-300">Smoking Preference</Label>
                <select
                  id="smoking_preference"
                  value={formData.smoking_preference}
                  onChange={(e) => handleInputChange('smoking_preference', e.target.value)}
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  <option value="Non-Smoking">Non-Smoking</option>
                  <option value="Smoking">Smoking</option>
                  <option value="Both">Both</option>
                </select>
              </div>

              <div>
                <Label htmlFor="status" className="text-gray-300">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="room_description" className="text-gray-300">Room Description</Label>
              <Textarea
                id="room_description"
                value={formData.room_description}
                onChange={(e) => handleInputChange('room_description', e.target.value)}
                placeholder="Describe the room features, amenities, and view..."
                rows={3}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {roomType ? 'Update' : 'Create'} Room Type
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </div>
    </div>
  );
};

export default RoomTypeModal;

